import {resolveRolloutPercent} from '../featureFlagRows';

/**
 * A 32-bit FNV-1a hash of the flag key and company id together.
 *
 * This is what makes a percentage rollout reproducible: the same flag orders the eligible pool the
 * same way on every modal open, so raising a target extends the selection a lower target made instead
 * of choosing a different set of companies. Mixing the flag key in means two flags at the same
 * percentage reach different companies, rather than every flag piling onto the same unlucky accounts.
 *
 * Do not change this function once flags have been rolled out against it. A different hash reorders
 * the pool, and a later target would then select companies a previous one had already skipped.
 */
export function hashFlagCompany(flagKey: string, companyId: string): number {
	const source = `${flagKey}:${companyId}`;
	let hash = 0x811c9dc5;
	for (let index = 0; index < source.length; index += 1) {
		hash ^= source.charCodeAt(index);
		// The FNV prime, applied with 32-bit multiplication that stays inside a double's exact range.
		hash = Math.imul(hash, 0x01000193) >>> 0;
	}
	return hash;
}

/**
 * Orderings already computed for a given eligible pool, cached by the pool's own `Set` identity so the
 * entry is freed once nothing else references that `Set`. A percentage rollout re-renders on every
 * slider tick with the same pool and the same flag, and the order never depends on the target
 * percentage — recomputing it on every tick wastes real work for no behavioural difference.
 */
const orderCache = new WeakMap<Set<string>, Map<string, string[]>>();

/**
 * The eligible companies in the order a percentage rollout fills them.
 *
 * Ties on the hash fall back to the company id, so the order is total and cannot depend on the order
 * the set happened to be built in. Each id's hash is computed once and carried into the sort rather
 * than recomputed on every comparison the sort makes of it.
 *
 * The result is cached per (flag, pool) pair: calling this again with the same flag key and the same
 * `Set` instance returns a fresh copy of the cached order instead of resorting. Always returns a copy,
 * cached or not, so a caller can never mutate the cache by mutating what they were handed.
 */
export function orderEligibleCompanyIds(flagKey: string, eligibleCompanyIds: Set<string>): string[] {
	const cachedForPool = orderCache.get(eligibleCompanyIds);
	const cached = cachedForPool?.get(flagKey);
	if (cached) {
		return cached.slice();
	}

	const hashed = Array.from(eligibleCompanyIds, companyId => ({companyId, hash: hashFlagCompany(flagKey, companyId)}));
	hashed.sort((a, b) => (a.hash !== b.hash ? a.hash - b.hash : a.companyId.localeCompare(b.companyId)));
	const ordered = hashed.map(entry => entry.companyId);

	const forPool = cachedForPool ?? new Map<string, string[]>();
	forPool.set(flagKey, ordered);
	orderCache.set(eligibleCompanyIds, forPool);

	return ordered.slice();
}

/**
 * The target percentage a requested value resolves to.
 *
 * The floor is the flag's current percentage: a rollout only goes up, because the modal adds grants
 * and never removes them, so a lower target could not be honoured anyway. A blank or unparseable
 * entry — which is what a cleared numeric input produces — resolves to the floor rather than to zero.
 */
export function clampTargetPercent(requested: number, currentPercent: number): number {
	if (!Number.isFinite(requested)) {
		return currentPercent;
	}
	return Math.min(100, Math.max(currentPercent, Math.floor(requested)));
}

export interface RolloutFillInput {
	flagKey: string;
	/** Encoded ids of the companies the percentage is measured against. */
	eligibleCompanyIds: Set<string>;
	/** Encoded ids of every company that already holds the flag, eligible or not. */
	grantedCompanyIds: Set<string>;
	/** Already clamped by `clampTargetPercent`. */
	targetPercent: number;
	/** Granted companies inside the eligible pool — the percentage's current numerator. */
	currentEligibleGranted: number;
}

/**
 * The companies to add to reach a target percentage, in fill order.
 *
 * The count is rounded up so a target is met rather than missed, and companies that already hold the
 * flag are skipped — taking the first N of the order without skipping would under-fill by however
 * many of them were already granted.
 *
 * A target at or below the current percentage always fills nothing, checked directly against the
 * granted count rather than trusting `needed <= 0` alone. The current percentage is itself a rounded
 * display value (e.g. 4 of 15 rounds to 27%), and re-deriving a company count from that rounded
 * percentage with the same ceiling used for a genuinely higher target can overshoot — `ceil(15 * 27 /
 * 100)` is 5, one more than the 4 actually granted — which would show an addition the modal opened
 * with, before the operator touched anything.
 */
export function computeFillCompanyIds({
	flagKey,
	eligibleCompanyIds,
	grantedCompanyIds,
	targetPercent,
	currentEligibleGranted,
}: RolloutFillInput): string[] {
	const currentPercent = resolveRolloutPercent(currentEligibleGranted, eligibleCompanyIds.size).percent;
	if (targetPercent <= currentPercent) {
		return [];
	}
	const wanted = Math.ceil((eligibleCompanyIds.size * targetPercent) / 100);
	const needed = wanted - currentEligibleGranted;
	if (needed <= 0) {
		return [];
	}
	return orderEligibleCompanyIds(flagKey, eligibleCompanyIds)
		.filter(companyId => !grantedCompanyIds.has(companyId))
		.slice(0, needed);
}

/**
 * The subset of the feature flag mutation's input this feature sends.
 *
 * The server deletes and re-inserts grants scoped to the ids named in the request, so which fields
 * are present decides the semantics — an unintended field is destructive rather than merely
 * redundant. Build one of these with a builder below rather than assembling it at a call site.
 */
export interface FeatureFlagAccessInput {
	featureFlag: string;
	companyId?: string;
	companyIds?: string[];
	allUsers?: boolean;
	persons?: string[];
	removeAccess?: boolean;
}

/**
 * Grant a flag to several companies at once, to everyone inside each.
 *
 * Carries no single `companyId`: naming one as well would have the server clear that company's named
 * users on top of the bulk grant. Additive — companies outside the list keep whatever they have, so
 * this sends only the companies being added and never the full set already at the target percentage.
 */
export function buildBulkAddInput(flagKey: string, companyIds: readonly string[]): FeatureFlagAccessInput {
	return {featureFlag: flagKey, companyIds: [...companyIds], allUsers: true};
}

/** Grant a flag to everyone in one company, replacing whatever that company had before. */
export function buildAllUsersInput(flagKey: string, companyId: string): FeatureFlagAccessInput {
	return {featureFlag: flagKey, companyId, allUsers: true};
}

/**
 * Set one company's flag access to exactly these people.
 *
 * The server replaces rather than merges, so `personIds` has to be the full list the company should
 * end up with. Passing only newly-added people would revoke everyone omitted.
 */
export function buildNamedUsersInput(flagKey: string, companyId: string, personIds: readonly string[]): FeatureFlagAccessInput {
	return {featureFlag: flagKey, companyId, persons: [...personIds]};
}

/**
 * Revoke several companies' access at once.
 *
 * The server routes this to its own bulk-removal path on seeing `removeAccess` together with
 * `companyIds`, deleting both the all-users grant and any named-user grants for every company named.
 * That routing is why `removeAccess` matters here where the single-company builder only documents
 * intent with it: without it the request would fall through to the additive path, whose `companyIds`
 * handling exists to serve bulk *adds*.
 *
 * Carries no singular `companyId`: the server would then take the single-company path for it as well,
 * deleting the same grants a second time.
 */
export function buildBulkRemoveInput(flagKey: string, companyIds: readonly string[]): FeatureFlagAccessInput {
	return {featureFlag: flagKey, companyIds: [...companyIds], removeAccess: true};
}

/**
 * Revoke one company's access.
 *
 * Naming the company with neither `allUsers` nor `persons` is what performs the revocation: the
 * server's delete runs and no insert follows it. `removeAccess` states the intent at the call site
 * and matches what the older feature flags page sends, but the server does not read it on this path —
 * unlike `buildBulkRemoveInput`, where it is what routes the request to the bulk-removal endpoint.
 */
export function buildRemoveAccessInput(flagKey: string, companyId: string): FeatureFlagAccessInput {
	return {featureFlag: flagKey, companyId, removeAccess: true};
}
